// Iter89 — Shopify checkout return / confirmation page.
// Shopify-hosted checkout returns the customer here with `checkout_id`
// (the LATM checkout draft id). We poll the backend every 3s until the
// Shopify order has synced into our local delivery_orders collection,
// then forward to the in-account order confirmation page.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { clearCart } from "@/lib/cart";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 1000 * 60 * 5; // 5 minutes

export default function CheckoutConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const checkoutId = params.get("checkout_id");
  const [phase, setPhase] = useState("polling");   // polling | paid | failed | not_found
  const [info, setInfo]  = useState(null);
  const [err, setErr]    = useState("");
  const pollRef = useRef(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!checkoutId) {
      setPhase("failed");
      setErr("Missing checkout_id in URL.");
      return;
    }
    let cancelled = false;

    async function tick() {
      try {
        const { data } = await api.get(
          `/shopify/checkout/status?checkout_id=${encodeURIComponent(checkoutId)}`
        );
        if (cancelled) return;
        if (!data?.found) {
          setPhase("not_found");
          return;
        }
        setInfo(data);
        if (data.status === "paid") {
          setPhase("paid");
          try { clearCart(); } catch (e) { /* ignore */ }
          setTimeout(() => {
            if (data.redirect_url) navigate(data.redirect_url);
          }, 1800);
          return;
        }
        // Still pending — schedule the next poll unless we've timed out.
        if (Date.now() - startedAt.current >= POLL_MAX_MS) {
          setPhase("timeout");
          return;
        }
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setErr(apiErrorMessage(e));
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [checkoutId, navigate]);

  return (
    <div
      className="min-h-[60vh] max-w-xl mx-auto px-4 py-16 text-center"
      data-testid="checkout-confirm"
    >
      {phase === "polling" && (
        <div data-testid="checkout-confirm-polling">
          <Loader2 className="h-10 w-10 mx-auto text-lifted-green animate-spin" />
          <h1 className="font-anton uppercase text-3xl text-white mt-6">
            Finalizing your order…
          </h1>
          <p className="text-lifted-dim text-sm mt-3">
            Shopify is confirming your payment. This usually takes a few seconds.
          </p>
          <p className="text-lifted-dim text-xs mt-2 font-mono">
            checkout_id: {checkoutId?.slice(0, 12)}…
          </p>
        </div>
      )}

      {phase === "paid" && (
        <div data-testid="checkout-confirm-paid">
          <CheckCircle2 className="h-12 w-12 mx-auto text-lifted-green" />
          <h1 className="font-anton uppercase text-3xl text-white mt-6">
            Payment received
          </h1>
          <p className="text-lifted-dim text-sm mt-3">
            Order {info?.shopify_order_number || info?.order_id?.slice(0, 8)} is
            being prepared. Redirecting…
          </p>
        </div>
      )}

      {phase === "not_found" && (
        <div data-testid="checkout-confirm-not-found">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-300" />
          <h1 className="font-anton uppercase text-3xl text-white mt-6">
            We can't find that checkout
          </h1>
          <p className="text-lifted-dim text-sm mt-3">
            We could not locate a Shopify checkout for this session. Please
            return to your cart to try again.
          </p>
          <Link to="/cart" className="btn-lifted mt-6 inline-flex" data-testid="checkout-confirm-back-to-cart">
            Back to cart
          </Link>
        </div>
      )}

      {phase === "timeout" && (
        <div data-testid="checkout-confirm-timeout">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-300" />
          <h1 className="font-anton uppercase text-3xl text-white mt-6">
            Still processing
          </h1>
          <p className="text-lifted-dim text-sm mt-3">
            Your payment is taking longer than expected to confirm. If you
            were charged, your order will appear in your account shortly.
          </p>
          <Link to="/account/orders" className="btn-lifted mt-6 inline-flex">
            View my orders
          </Link>
        </div>
      )}

      {phase === "failed" && (
        <div data-testid="checkout-confirm-failed">
          <AlertCircle className="h-12 w-12 mx-auto text-rose-300" />
          <h1 className="font-anton uppercase text-3xl text-white mt-6">
            Something went wrong
          </h1>
          <p className="text-lifted-dim text-sm mt-3">{err || "Unknown error."}</p>
          <Link to="/cart" className="btn-lifted mt-6 inline-flex">
            Back to cart
          </Link>
        </div>
      )}
    </div>
  );
}
